import React, { useState, useMemo, useRef, useCallback } from 'react';
import classnames from 'classnames';
import { ResponsiveLine } from '@nivo/line';

import Button from '../components/Button';
import Lazy from '../components/Lazy';
import MultiSelect, { arrayToOptions } from '../components/MultiSelect';
import {
	axisRight,
	YearLinesLayer,
	ZeroLineLayer,
	ZScoreHorizontalLayers,
	UncertaintyLayer,
	theme,
	getToolTip,
} from '../components/NivoHelpers';
import RangeSelector from '../components/RangeSelector';
import Section, { Full, Left, Right, Sticky } from '../components/Section';
import Select from '../components/Select';
import WhatZScore from '../components/WhatZScore';

import { getAgeOptions, uid, downloadFile } from '../utils/data';
import {
	calculateWeekTickLabels,
	calculateWeekTickValues,
	filterByKey,
	sliceGroupsData,
	countGroupsMinMax,
	prettyNumber,
	getZByCountryCSV,
} from '../utils/graphs';

import cssVars from '../styles/cssVars';
import shared from '../styles/shared.module.css';
import css from './ZByCountry.module.css';

export const ZByCountry = ({ data, ages, printing }) => {
	const { weeks } = data;
	const id = useMemo(() => uid('z'), []);
	const countryOptions = useMemo(() => arrayToOptions(data.data.map((country) => country.id)), [data]);
	const [range, setRange] = useState({ min: weeks.length - 52 * 4, max: weeks.length - 1 });
	const ageOptions = useMemo(() => getAgeOptions(ages), [ages]);
	const [selectedAge, setAge] = useState(ageOptions[0]);
	const [selectedCountries, setSelectedCountries] = useState(countryOptions);
	const [saving, setSaving] = useState(false);
	const graphRef = useRef(null);
	const onCountriesChange = (options) => {
		setSelectedCountries(options);
	};

	// Find the selected age group
	let filtered = useMemo(() => {
		const newData = [];
		for (let i = 0; i < data.data.length; i++) {
			const group = data.data[i].data.find((d) => d.id === selectedAge.value);
			if (group && group.data) {
				newData.push({
					id: data.data[i].id,
					data: group.data,
				});
			}
		}
		return newData;
	}, [selectedAge, data]);

	// Filter by country and time range
	filtered = useMemo(() => {
		return filterByKey(
			filtered,
			'id',
			selectedCountries.map((c) => c.value)
		);
	}, [selectedCountries, filtered]);

	filtered = useMemo(() => sliceGroupsData(filtered, range.min, range.max), [filtered, range]);

	const [minY, maxY, minMaxVals, minX, maxX] = useMemo(() => countGroupsMinMax(filtered, true), [filtered]);

	// Prepare the tick values for the x-axis
	const xTickLabels = useMemo(() => calculateWeekTickLabels(weeks), [weeks]);
	const xTickValues = useMemo(() => calculateWeekTickValues(filtered, weeks, 10), [weeks, filtered]);

	const graphs = [];
	for (let i = 0; i < filtered.length; i++) {
		const chartHeight = filtered.length < 4 ? 185 : 125;
		graphs.push(
			<Lazy key={`${id}-${filtered[i].id}`} height={chartHeight + 50} show={printing || saving}>
				<Graph
					id={id}
					height={chartHeight}
					item={filtered[i]}
					weeks={weeks}
					range={range}
					minY={minY}
					maxY={maxY}
					minX={minX}
					maxX={maxX}
					xTickValues={xTickValues}
					xTickLabels={xTickLabels}
				/>
			</Lazy>
		);
	}

	const onDownloadData = useCallback(
		(filename) => {
			const csv = getZByCountryCSV(filtered, weeks, selectedAge);
			downloadFile(filename + '.csv', csv);
		},
		[filtered, weeks, selectedAge]
	);

	return (
		<Section id={'z-scores-by-country'} saving={saving}>
			<Full title={'Z-scores by country'}>
				<p>
					Graphs of the weekly z-score at the national level in the EuroMOMO partner countries and subnational
					regions.
				</p>
				<WhatZScore />
			</Full>
			<Left
				filename={'charts-z-scores-by-country'}
				imageRef={graphRef}
				setSaving={setSaving}
				onDownloadData={onDownloadData}
				sticky>
				<Select
					hideOnPrint
					className={css.select}
					label="Age group"
					value={selectedAge}
					options={ageOptions}
					onChange={setAge}
				/>
				<MultiSelect
					hideOnPrint
					isSearchable
					label={'Countries'}
					className={css.select}
					options={countryOptions}
					value={selectedCountries}
					onChange={onCountriesChange}
				/>
			</Left>
			<Right forwardRef={graphRef}>
				<Sticky>
					<RangeSelector
						label={'Week range'}
						className={classnames(css.range, { [shared.hidden]: saving })}
						min={0}
						max={weeks.length - 1}
						value={range}
						onChange={setRange}
						formatLabel={(v) => weeks[v]}
						showYearLabels
						fps={30}
					/>
					<div className={shared.legend}>
						<div className={shared.item}>
							<span className={shared.line} />
							<h5>Z-score</h5>
						</div>
						<div className={shared.item}>
							<span
								className={shared.line}
								style={{ borderColor: cssVars.baselineBlack, borderStyle: 'dashed', borderWidth: 1 }}
							/>
							<h5>Baseline</h5>
						</div>
						<div className={shared.item}>
							<span
								className={shared.line}
								style={{ borderColor: cssVars.normalRangeGray, borderWidth: 15 }}
							/>
							<h5>Normal range</h5>
						</div>
						<div className={shared.item}>
							<span
								className={shared.line}
								style={{ borderColor: cssVars.highlightRed, borderStyle: 'dashed', borderWidth: 1 }}
							/>
							<h5>Substantial increase</h5>
						</div>
						<div className={shared.item}>
							<span
								className={shared.line}
								style={{ borderColor: cssVars.uncertaintyYellow, borderWidth: 15 }}
							/>
							<h5>Corrected for delay in registration</h5>
						</div>
					</div>
				</Sticky>
				<div className={shared.graphsContainer}>{graphs}</div>
			</Right>
		</Section>
	);
};

export default ZByCountry;

const margin = { top: 7, right: 10, bottom: 18, left: 35 };
const xScale = { type: 'linear', min: 'auto', max: 'auto' };
const colors = [cssVars.dataBlue];
const pointColor = { theme: 'background' };
const pointBorderColor = { from: 'serieColor' };
const layers = [
	'grid',
	'markers',
	'areas',
	YearLinesLayer,
	ZeroLineLayer,
	ZScoreHorizontalLayers,
	'lines',
	'axes',
	'points',
	UncertaintyLayer,
	'legends',
	'mesh',
	'slices',
];
const legends = [];

const Graph = ({ item, id, height, weeks, minY, maxY, minX, maxX, range, xTickValues, xTickLabels }) => {
	return (
		<div className={css.chartContainer}>
			<h4>{item.id}</h4>
			<div style={{ height }} className={classnames(css.chart)}>
				<ResponsiveLine
					key={item.id}
					id={id}
					data={item.data}
					range={range}
					weeks={weeks}
					minY={minY}
					maxY={maxY}
					minX={minX}
					maxX={maxX}
					tooltip={({ point }) => getToolTip(weeks[point.data.x], prettyNumber(point.data.yFormatted, true))}
					margin={margin}
					xScale={xScale}
					yScale={{ type: 'linear', min: minY, max: maxY }}
					axisTop={null}
					axisRight={axisRight}
					axisBottom={{
						orient: 'bottom',
						tickSize: 5,
						tickPadding: 0,
						legend: '',
						legendOffset: 56,
						legendPosition: 'middle',
						tickValues: xTickValues,
						format: (weekIdx) => xTickLabels[weekIdx],
					}}
					axisLeft={{
						format: (num) => prettyNumber(num, true, true),
						orient: 'left',
						tickSize: 5,
						tickValues: 4,
						tickPadding: 5,
						legend: '',
						legendOffset: 0,
						legendPosition: 'middle',
					}}
					crosshairType={'x'}
					enableGridX={false}
					enableGridY={true}
					gridYValues={4}
					colors={colors}
					lineWidth={2}
					pointSize={2}
					pointColor={pointColor}
					pointBorderWidth={2}
					pointBorderColor={pointBorderColor}
					pointLabel="y"
					pointLabelYOffset={-12}
					useMesh={true}
					enablePoints={false}
					animate={false}
					theme={theme}
					layers={layers}
					legends={legends}
				/>
			</div>
		</div>
	);
};
